import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
// Ícones sempre na variante outline (padrão do painel)
import {
  HiOutlineTrendingUp,
  HiOutlineBadgeCheck,
  HiOutlineCash,
  HiOutlineClock,
  HiOutlineUserGroup,
  HiOutlineUserAdd,
  HiOutlineCake,
  HiOutlineUsers,
} from "react-icons/hi";
import { PageTopBreadcrumb } from "../PageTopBreadcrumb";
import { useDashboardData } from "../dashboard/useDashboardData";
import { DashboardHeader } from "../dashboard/DashboardHeader";
import { StatCard } from "../dashboard/StatCard";
import { MembersStatusDonut } from "../dashboard/MembersStatusDonut";
import { GrowthBarChart } from "../dashboard/GrowthBarChart";
import { CancellationsBarChart } from "../dashboard/CancellationsBarChart";
import { CategoryBarChart } from "../dashboard/CategoryBarChart";
import { RevenueBarChart } from "../dashboard/RevenueBarChart";
import { RevenueCategoryChart } from "../dashboard/RevenueCategoryChart";
import { RecentMembers } from "../dashboard/RecentMembers";
import { colors } from "../../data/tokens";

const ICON_SIZE = 22;

export const DashboardPage = (_props: any) => {
  const data = useDashboardData();

  // Adimplência: sócios ATIVOS em dia ÷ ativos — numerador e denominador
  // no mesmo recorte (o servidor recalcula a coluna userEnabled em
  // background; defasagem de até ~3 min)
  const active = data.statusCounts?.active ?? null;
  const adimplentPercent =
    data.adimplentCount !== null && active
      ? Math.round((data.adimplentCount / active) * 100)
      : null;

  // Variação de novos sócios vs mês anterior
  const delta =
    data.newThisMonth !== null && data.newPrevMonth !== null
      ? {
          percent:
            data.newPrevMonth > 0
              ? ((data.newThisMonth - data.newPrevMonth) / data.newPrevMonth) *
                100
              : data.newThisMonth > 0
              ? 100
              : 0,
          label: "vs mês anterior",
        }
      : null;

  return (
    <>
      <PageTopBreadcrumb title="Dashboard" firstPath="/#/dashboard" />
      <Box sx={{ mb: 3 }}>
        <DashboardHeader />
        <Grid container spacing={2.5} alignItems="stretch">
          {/* Linha 1 — indicadores de sócios e atividade */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Sócios cadastrados"
              value={data.totalMembers}
              icon={<HiOutlineUserGroup size={ICON_SIZE} color={colors.primary} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Adimplência"
              subtitle={
                data.adimplentCount !== null && active
                  ? `${data.adimplentCount.toLocaleString(
                      "pt-BR"
                    )} de ${active.toLocaleString("pt-BR")} ativos`
                  : "ativos em dia ÷ ativos"
              }
              value={adimplentPercent}
              suffix="%"
              icon={
                <HiOutlineBadgeCheck size={ICON_SIZE} color={colors.primary} />
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Novos sócios no mês"
              value={data.newThisMonth}
              delta={delta}
              icon={<HiOutlineUserAdd size={ICON_SIZE} color={colors.primary} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Aniversariantes do mês"
              value={data.birthdayCount}
              icon={<HiOutlineCake size={ICON_SIZE} color={colors.primary} />}
            />
          </Grid>

          {/* Linha 2 — órfãos, pendentes, receitas e o resumo do
              semestre. Os
              cards de Assinaturas, Isenções e formas de pagamento
              (Boletos, Boletos Iugu, Pix, Cartão) saíram: os mesmos
              recortes já vivem nos KPIs da tela de Transações — aqui
              eram duplicação e mais uma dúzia de consultas por
              abertura. "Despesas" já tinha saído (a base não tem uma
              despesa registrada) e "Contatos recebidos" virou o badge
              do item Contatos no menu lateral. */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Membros órfãos"
              value={data.orphanedCount}
              icon={<HiOutlineUsers size={ICON_SIZE} color={colors.primary} />}
            />
          </Grid>
          {/* Fila de trabalho do admin: todo cadastro feito pelo site
              nasce Pendente até a aprovação — o número já vem no
              statusCounts do donut, nenhuma consulta extra */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Cadastros pendentes"
              subtitle="aguardando aprovação"
              value={data.statusCounts ? data.statusCounts.pending : null}
              icon={<HiOutlineClock size={ICON_SIZE} color={colors.primary} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Receitas"
              subtitle="transações registradas"
              value={data.incomeCount}
              icon={
                <HiOutlineTrendingUp size={ICON_SIZE} color={colors.primary} />
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Receita no semestre"
              subtitle={data.revenue?.semester.currentLabel || "por data de referência"}
              value={data.revenue ? data.revenue.semester.currentTotal : null}
              money
              delta={
                data.revenue?.semester.variationPct != null
                  ? {
                      percent: data.revenue.semester.variationPct,
                      label: "vs semestre anterior completo",
                    }
                  : null
              }
              icon={<HiOutlineCash size={ICON_SIZE} color={colors.primary} />}
            />
          </Grid>

          {/* Linha 3 — receita mensal (fase 2 dos KPIs, endpoint de
              agregação read-only; só type INCOME) ao lado dos últimos
              cadastros */}
          <Grid item xs={12} md={7}>
            <RevenueBarChart monthly={data.revenue?.monthly ?? null} />
          </Grid>
          <Grid item xs={12} md={5}>
            <RecentMembers recent={data.recent} />
          </Grid>

          {/* Linha 4 — entradas × saídas de sócios lado a lado: novos
              em azul, cancelamentos em vermelho */}
          <Grid item xs={12} md={6}>
            <GrowthBarChart monthly={data.monthly} />
          </Grid>
          <Grid item xs={12} md={6}>
            <CancellationsBarChart monthly={data.cancellations} />
          </Grid>

          {/* Linha 5 — distribuição dos sócios: categorias e situação */}
          <Grid item xs={12} md={7}>
            <CategoryBarChart categories={data.categories} />
          </Grid>
          <Grid item xs={12} md={5}>
            <MembersStatusDonut counts={data.statusCounts} />
          </Grid>

          {/* Linha 6 — receita por categoria em largura total: barras
              horizontais com nomes longos precisam do espaço (meio a
              meio os rótulos truncavam) */}
          <Grid item xs={12}>
            <RevenueCategoryChart
              categories={data.revenue?.byCategory ?? null}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
